import { Alert, Card } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createUser, getUserById } from "../../../services/userService";
import { useUpdateUser } from "../../../hooks/userHooks";
import UserForm from "./UserForm";
import { useToast } from '../../ui/Toast/ToastProvider';

const User = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const isEdit = !!id && id !== 'add';

  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const { updateUser, loading: updating, error: updateError, setError: setUpdateError } = useUpdateUser();

  useEffect(() => {
    const load = async () => {
      if (!isEdit) {
        setUserInfo(null);
        return;
      }

      try {
        setLoadingUser(true);
        const user = await getUserById(id);
        setUserInfo(user);
      } catch (e) {
        setUpdateError(e?.message || "Failed to load user.");
      } finally {
        setLoadingUser(false);
      }
    };

    load();
  }, [id, isEdit, setUpdateError]);

  const submit = async (payload) => {
    setCreateError("");
    setUpdateError("");

    try {
      if (!isEdit) {
        setCreating(true);
        await createUser(payload);
        showToast("User created successfully.", "success");
      } else {
        await updateUser(userInfo?.id ?? id, payload);
        showToast("User updated successfully.", "success");
      }

      navigate("/users");
    } catch (e) {
      if (!isEdit) {
        setCreateError(e?.message || "Failed to create user.");
      }
    } finally {
      setCreating(false);
    }
  };

  const saving = creating || updating;
  const error = createError || updateError;

  return (
    <Card>
      <Card.Header>
        <h5>
          <a href="/users">Users</a> / {isEdit ? "Edit User" : "Add User"}
        </h5>
      </Card.Header>

      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <UserForm
          mode={isEdit ? "edit" : "create"}
          initialValues={userInfo}
          saving={saving}
          onSubmit={submit}
          onCancel={() => navigate("/users")}
          loading={loadingUser}
        />
      </Card.Body>
    </Card>
  );
};

export default User;
